import { ReactElement } from 'react'
import AccountCardMFE from 'account-card-mfe'
import getMfeConfig from 'helpers/getMFEConfig'
import { getEssLinkDisabledClients } from 'helpers/isAccountLinkDisabledForAClient'
import { AccountCardHandlers } from 'account-card-mfe/lib/mfe/AccountCardMFE/AccountCardMFE.types'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { getBoolFromApptimize } from 'helpers/getBoolFromApptimize'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import useCTAHandlers from '../actions/useCTAHandlers'
import { CardColumn } from '../subcomponents'

interface AccountCardProps {
  accountId: string
  cardIndex: number
  displayName?: string
  accountLast4?: string
}

const AccountCard = ({
  accountId,
  cardIndex,
  displayName,
  accountLast4
}: AccountCardProps): ReactElement => {
  const mfeConfig = getMfeConfig('mfe')

  const isDigitalCollectionsExperienceEnabled = isFeatureFlagEnabled(
    'isDigitalCollectionsExperienceEnabled'
  )
  const isLargeAutopayIndicatorEnabled = isFeatureFlagEnabled(
    'isLargeAutopayIndicatorEnabled'
  )
  const isLoanNickNameModalEnabled = isFeatureFlagEnabled(
    'isLoanNickNameModalEnabled'
  )
  const isSettlementPlanEnabled = isFeatureFlagEnabled(
    'isSettlementPlanEnabled'
  )

  const showActivityForSecuredInstallment = isFeatureFlagEnabled(
    'showActivityForSecuredInstallment'
  )

  const shouldShowMinPaymentTooltip = getBoolFromApptimize(
    'shouldShowMinPaymentTooltip'
  )

  const shouldShowMinPaymentTooltipV2 = getBoolFromApptimize(
    'shouldShowMinPaymentTooltipV2'
  )

  const brandRefreshPhase = getBrandRefreshPhase()

  const handlers = useCTAHandlers(accountId)

  const eventHandlers: AccountCardHandlers = {
    handleActivityClick: handlers.handleActivityClick,
    handleDetailsClick: handlers.handleDetailsClick,
    handleMAPClick: handlers.handleMAPClick,
    handleAccountServicesClick: handlers.handleAccountServicesClick,
    handleRewardsClick: handlers.handleRewardsClick,
    handleStatementsClick: handlers.handleStatementsClick,
    handleCBRClick: handlers.handleCBRClick,
    handlePromotionsClick: handlers.handlePromotionsClick,
    handleRegisterAccountClick: handlers.handleRegisterAccountClick,
    handleViewPaymentPlanOrOption: handlers.handleCollectionClick,
    handleAutopayClick: handlers.handleAutopayClick
  }

  const ariaLabel =
    displayName && accountLast4
      ? `${displayName} ending in ${accountLast4}`
      : `Account card ${cardIndex + 1}`

  return (
    <CardColumn $cardIndex={cardIndex} aria-label={ariaLabel}>
      <AccountCardMFE
        isQueryClientPresent
        mfeConfig={mfeConfig}
        accountId={accountId}
        essDisabledClients={getEssLinkDisabledClients()}
        featureFlags={{
          isAccountUpgradingMessageEnabled: false,
          isCardActivationEnabled: false,
          isCBREnabled: true,
          isDigitalCollectionsExperienceEnabled,
          isLargeAutopayIndicatorEnabled,
          isLoanNickNameModalEnabled,
          isSettlementPlanEnabled,
          isNotificationsEnabled: true,
          shouldShowMinPaymentTooltip,
          shouldShowMinPaymentTooltipV2,
          showActivityForSecuredInstallment,
          brandRefreshPhase
        }}
        eventHandlers={eventHandlers}
        handleAccountLink={handlers.handleAccountClick}
        shouldCallCreditAccountsQuery
      />
    </CardColumn>
  )
}

export default AccountCard
